import {
  Component,
  SimpleChanges,
  Inject,
  Input,
  NgZone,
  PLATFORM_ID,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { areJSONEqual } from '../../../utils/helper';

@Component({
  selector: 'MultiLineChart',
  templateUrl: './multi-line-chart.component.html',
  styleUrls: ['./multi-line-chart.component.scss'],
})
export class MultiLineChartComponent implements AfterViewInit, OnDestroy {
  @Input() props: any;

  private root!: am5.Root;
  private chart!: am5xy.XYChart;
  private xAxis!: am5xy.DateAxis<am5xy.AxisRendererX>;
  private yAxis!: am5xy.ValueAxis<am5xy.AxisRendererY>;
  private legend!: am5.Legend;
  private series: am5xy.LineSeries[] = [];

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currData = changes['props']?.currentValue
    const prevData = changes['props']?.previousValue

    if (!areJSONEqual(currData, prevData ? prevData : {})) {
      this.createSeries()
    }
  }

  ngOnDestroy(): void {
    this.cleanupChart();
  }

  private initChart(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.root = am5.Root.new('multi-line-chart-div');
      this.root.setThemes([am5themes_Animated.new(this.root)]);

      this.chart = this.root.container.children.push(
        am5xy.XYChart.new(this.root, {
          panX: true,
          panY: true,
          wheelX: 'zoomX',
          wheelY: 'zoomY',
          pinchZoomX: true,
          pinchZoomY: true,
        })
      );

      this.createAxes();
      this.addCursor();
      this.addScrollbars();
      this.addLegend();
      this.createSeries();
    });
  }

  private createAxes(): void {
    this.xAxis = this.chart.xAxes.push(
      am5xy.DateAxis.new(this.root, {
        baseInterval: { timeUnit: "day", count: 1 },
        renderer: am5xy.AxisRendererX.new(this.root, {}),
        tooltip: am5.Tooltip.new(this.root, {})
      })
    );

    this.yAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {})
      })
    );
  }

  private addCursor(): void {
    this.chart.set("cursor", am5xy.XYCursor.new(this.root, {
      behavior: "zoomX",
      xAxis: this.xAxis
    }));
  }

  private addScrollbars(): void {
    this.chart.set("scrollbarX", am5.Scrollbar.new(this.root, {
      orientation: "horizontal"
    }));

    this.chart.set("scrollbarY", am5.Scrollbar.new(this.root, {
      orientation: "vertical"
    }));
  }

  private addLegend(): void {
    this.legend = this.chart.rightAxesContainer.children.push(
      am5.Legend.new(this.root, {
        width: 180,
        paddingLeft: 15,
        height: am5.percent(100),
        layout: this.root.verticalLayout
      })
    );
  }

  private sortDataByDate(data: Array<{ date: string | Date; value: number }>): Array<{ date: number; value: number }> {
    return data
      .map(item => ({
        ...item,
        date: item.date instanceof Date ? item.date.getTime() : new Date(item.date).getTime()
      }))
      .sort((a, b) => a.date - b.date);
  }

  private createSeries(): void {
    // Clear existing series
    this.series.forEach(series => series.dispose());
    this.series = [];

    if (!this.props) return;

    // Your custom color palette
    const colorPalette = [
      "#ac1d54", // Deep pink
      "#1078c0", // Blue
      "#F2AA6B", // Peach
      "#3a7d44", // Green (additional)
      "#6a4c93"  // Purple (additional)
    ];

    // Convert hex strings to am5.Color objects
    const colors = colorPalette.map(hex => am5.color(hex));

    Object.entries(this.props).forEach(([name, data], index) => {
      if (!data || !Array.isArray(data)) return;

      // Sort data by date
      const sortedData = this.sortDataByDate(data);

      // Create series with custom color (cycling through palette)
      const seriesColor = colors[index % colors.length];

      const series = this.chart.series.push(
        am5xy.LineSeries.new(this.root, {
          name: name,
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          valueYField: "value",
          valueXField: "date",
          tooltip: am5.Tooltip.new(this.root, {
            labelText: "[bold]{name}[/]\n{valueY}",
            background: am5.Rectangle.new(this.root, {
              fill: seriesColor,
              fillOpacity: 0.2
            })
          }),
          stroke: seriesColor,
          fill: seriesColor
        })
      );

      // Add bullets for better visibility
      series.bullets.push(() => {
        return am5.Bullet.new(this.root, {
          sprite: am5.Circle.new(this.root, {
            radius: 4,
            fill: seriesColor,
            stroke: am5.color(0xffffff),
            strokeWidth: 2
          })
        });
      });

      series.data.setAll(sortedData);
      this.series.push(series);
    });

    // Update legend with custom styling
    if (this.legend) {
      this.legend.data.setAll(this.series);
      this.legend.labels.template.setAll({
        fill: am5.color(0x333333),
        fontSize: "0.8em"
      });
      this.legend.markers.template.setAll({
        width: 15,
        height: 15
      });
    }
  }

  private updateChart(): void {
    this.zone.runOutsideAngular(() => {
      this.createSeries();
    });
  }

  private cleanupChart(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        if (this.root) {
          this.root.dispose();
        }
      });
    }
  }
}
